import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];
  /** @type {ReturnType<typeof setTimeout>[]} */
  #reconciliationTimers = [];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.addEventListener('theme:cart-optimistic-update', this.onOptimisticCartUpdate);
    document.addEventListener('theme:cart-reconciled', this.onCartReconciled);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#clearReconciliationTimers();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.removeEventListener('theme:cart-optimistic-update', this.onOptimisticCartUpdate);
    document.removeEventListener('theme:cart-reconciled', this.onCartReconciled);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount = cart?.totalQuantity ?? detail?.itemCount ?? 0;

        this.renderCartBubble(itemCount);
        this.#scheduleCartCountReconciliation();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Shows the expected cart count as soon as an add-to-cart request is sent.
   * The CartLinesUpdateEvent later replaces this with Shopify's actual total.
   * @param {CustomEvent<{delta?: number}>} event
   */
  onOptimisticCartUpdate = (event) => {
    const delta = Number(event.detail?.delta);
    if (!Number.isFinite(delta) || delta === 0) return;

    const currentCount = Number.isFinite(this.currentCartCount) ? this.currentCartCount : 0;
    this.renderCartBubble(Math.max(0, currentCount + delta));
  };

  /**
   * Applies the absolute cart count after delayed gift-app reconciliation.
   * @param {CustomEvent<{itemCount?: number}>} event
   */
  onCartReconciled = (event) => {
    const itemCount = Number(event.detail?.itemCount);
    if (!Number.isFinite(itemCount) || itemCount < 0) return;

    this.renderCartBubble(itemCount, false);
  };

  #clearReconciliationTimers() {
    this.#reconciliationTimers.forEach((timer) => clearTimeout(timer));
    this.#reconciliationTimers = [];
  }

  /**
   * Gift apps can add or remove lines after the original Shopify response.
   * The header survives cart section replacement, so reconcile here as well.
   */
  #scheduleCartCountReconciliation() {
    this.#clearReconciliationTimers();

    for (const delay of [1000, 3500, 7500]) {
      const timer = setTimeout(async () => {
        try {
          const response = await fetch('/cart.js', {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          });
          if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);

          const cart = await response.json();
          this.renderCartBubble(Number(cart?.item_count) || 0, false);
        } catch (error) {
          console.warn('[cart-icon] Cart count reconciliation failed:', error);
        }
      }, delay);

      this.#reconciliationTimers.push(timer);
    }
  }

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
